/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * API Service - Simple functions for API calls
 */

import {
  addCompletedStep,
  addMessage,
  removeSkippedStep,
  setParsedTemplates,
} from '../data-store/copilotSlice';
import { buildConnectionJSONTemplate } from '../utils/connectionUtils';
import { replacePlaceholders } from '../utils/placeholderUtils';

interface OnboardResult {
  status?: number;
  message?: string;
  endpointKey?: number;
  endpointSyncStatus?: string;
  data?: {
    endpointKey?: number;
    endpointSyncStatus?: string;
  };
}

interface ApiServices {
  initiateOnboard?: (payload: unknown) => Promise<unknown>;
  testConnection?: (payload: unknown) => Promise<unknown>;
  saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
}

/**
 * Process form data for API payload (For Basic form update)
 */
export const processFormDataForAPI = (
  formData: Record<string, unknown>,
  applicationtype: string
): Record<string, unknown> => {
  const apiPayload: Record<string, unknown> = {
    multipleconnection: false,
    'externalconnectiontype.connectionType': applicationtype,
  };

  Object.entries(formData).forEach(([key, value]) => {
    if (key === 'endpoints.ownerkey') {
      // Handle string, array, and object formats
      if (
        typeof value === 'string' &&
        (value.startsWith('[') || value.startsWith('{'))
      ) {
        try {
          const parsed = JSON.parse(value);

          if (Array.isArray(parsed) && parsed.length > 0) {
            apiPayload[key] = String(
              parsed[0].id || parsed[0].value || parsed[0]
            );
          } else {
            apiPayload[key] = String(parsed.id || parsed.value || value);
          }
        } catch {
          apiPayload[key] = String(value);
        }
      } else if (Array.isArray(value) && value.length > 0) {
        const firstItem = value[0] as { id?: string; value?: string };

        apiPayload[key] = String(firstItem.id || firstItem.value || firstItem);
      } else if (typeof value === 'object' && value !== null) {
        const obj = value as { id?: string; value?: string };

        apiPayload[key] = String(obj.id || obj.value || '');
      } else {
        apiPayload[key] = String(value);
      }
    } else if (key === 'endpoints.ownerType') {
      apiPayload[key] = Array.isArray(value) ? value : [String(value)];
    } else if (value !== undefined && value !== null && value !== '') {
      apiPayload[key] = value;
    }
  });

  return apiPayload;
};

export const saveUseCaseConfiguration = async (
  services: ApiServices,
  createAccountData: Record<string, unknown>,
  endpointKey: number,
  userPrompt?: string
): Promise<unknown> => {
  if (!services.saveUseCaseConfiguration) {
    throw new Error('saveUseCaseConfiguration service not available');
  }

  // Replace @CUAPROMPT@ in CreateAccountJSON with escaped user prompt
  const processedData = replacePlaceholders(createAccountData, userPrompt);
  const createAccountJSON = JSON.stringify(processedData);

  const payload = {
    applicationtype: 'CUA',
    endpointKey,
    body: {
      multipleconnection: false,
      'externalconnattvalue.CreateAccountJSON': createAccountJSON,
    },
  };

  return services.saveUseCaseConfiguration(payload);
};

export const saveAccountConfiguration = async (
  services: ApiServices,
  stepName:
    | 'CreateAccount'
    | 'UpdateAccount'
    | 'EnableAccount'
    | 'DisableAccount'
    | 'RemoveAccount',
  accountData: Record<string, unknown>,
  endpointKey: number
): Promise<unknown> => {
  if (!services.saveUseCaseConfiguration) {
    throw new Error('saveUseCaseConfiguration service not available');
  }

  const jsonFieldMap = {
    CreateAccount: 'externalconnattvalue.CreateAccountJSON',
    UpdateAccount: 'externalconnattvalue.UpdateAccountJSON',
    EnableAccount: 'externalconnattvalue.EnableAccountJSON',
    DisableAccount: 'externalconnattvalue.DisableAccountJSON',
    RemoveAccount: 'externalconnattvalue.RemoveAccountJSON',
  };

  const jsonField = jsonFieldMap[stepName];

  const payload = {
    applicationtype: 'CUA',
    endpointKey,
    body: {
      multipleconnection: false,
      [jsonField]: accountData,
    },
  };

  return services.saveUseCaseConfiguration(payload);
};

export const saveAccountConfigurationForEdit = async (
  services: ApiServices,
  dispatch: any,
  getState: () => unknown,
  stepName:
    | 'CreateAccount'
    | 'UpdateAccount'
    | 'EnableAccount'
    | 'DisableAccount'
    | 'RemoveAccount',
  accountJSON: string,
  endpointKey: number,
  userPrompt?: string
): Promise<void> => {
  if (!services.saveUseCaseConfiguration) {
    throw new Error('saveUseCaseConfiguration service not available');
  }

  const jsonFieldMap = {
    CreateAccount: 'externalconnattvalue.CreateAccountJSON',
    UpdateAccount: 'externalconnattvalue.UpdateAccountJSON',
    EnableAccount: 'externalconnattvalue.EnableAccountJSON',
    DisableAccount: 'externalconnattvalue.DisableAccountJSON',
    RemoveAccount: 'externalconnattvalue.RemoveAccountJSON',
  };

  const jsonField = jsonFieldMap[stepName];
  let processedJSON = accountJSON;

  if (userPrompt) {
    const parsedData = JSON.parse(accountJSON) as Record<string, unknown>;
    const processedData = replacePlaceholders(parsedData, userPrompt);

    processedJSON = JSON.stringify(processedData);
  }

  const payload = {
    applicationtype: 'CUA',
    endpointKey,
    body: {
      multipleconnection: false,
      [jsonField]: processedJSON,
    },
  };

  try {
    const result = await services.saveUseCaseConfiguration(payload);

    // RTK Query mutations return { data, error } object
    if (
      result &&
      typeof result === 'object' &&
      'error' in result &&
      result.error
    ) {
      throw new Error('API call failed');
    }

    // Check for HTTP 500 status code in the result data
    if (result && typeof result === 'object' && 'data' in result) {
      const { data } = result;

      if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 500
      ) {
        throw new Error('API returned 500 status code');
      }
    }

    const state = getState() as {
      copilot?: { parsedTemplates?: Record<string, unknown> };
    };
    const currentParsedTemplates = state.copilot?.parsedTemplates || {};

    // Only store prompts and show success on actual API success
    dispatch(
      setParsedTemplates({
        ...currentParsedTemplates,
        [stepName]: {
          ...(currentParsedTemplates[stepName] as Record<string, unknown>),
          instructions: userPrompt || '',
        },
      })
    );

    dispatch(addCompletedStep(stepName));
    dispatch(removeSkippedStep(stepName));

    dispatch(
      addMessage({
        id: `success-${Date.now()}`,
        type: 'ai_response',
        subType: 'success',
        text: 'The account configuration has been successfully updated.',
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    dispatch(
      addMessage({
        id: `error-${Date.now()}`,
        type: 'ai_response',
        subType: 'error',
        timestamp: Date.now(),
      })
    );
    throw error;
  }
};

export const initiateOnboard = async (
  services: ApiServices,
  generalData: Record<string, unknown>,
  connectionData: Record<string, unknown>,
  parsedTemplates: any,
  appTypeKey = 85,
  draftType = 0,
  userPrompt?: string,
  categoryTemplates?: any
): Promise<OnboardResult> => {
  if (!services.initiateOnboard) {
    throw new Error('Initiate onboard API not available');
  }

  const endpointName = String(generalData['securitysystems.systemname'] || '');

  // Build General category
  const generalUncommittedValues: Record<string, any> = {};
  const generalFields = parsedTemplates?.General || [];

  generalFields.forEach((field: any) => {
    if (field.attributeName && generalData[field.attributeName] !== undefined) {
      const key = String(field.attributeKey);
      let value = generalData[field.attributeName];

      // Handle owner key format
      if (
        field.attributeName === 'endpoints.ownerkey' &&
        typeof value === 'string' &&
        value.startsWith('{')
      ) {
        try {
          value = JSON.parse(value);
        } catch {
          // Keep as string if parsing fails
        }
      }

      // Handle ownerType array format
      if (field.attributeName === 'endpoints.ownerType') {
        value = Array.isArray(value) ? value : [String(value)];
      }

      generalUncommittedValues[key] = {
        submitId: field.attributeName,
        value,
        attributeKey: field.attributeKey,
        identifiers: {
          group: 'General',
          category: 'General',
          subcategory: 'Configuration',
        },
        // eslint-disable-next-line no-nested-ternary
        name:
          // eslint-disable-next-line no-nested-ternary
          field.attributeName === 'endpoints.ownerkey'
            ? 'Owner'
            : field.attributeName === 'endpoints.ownerType'
            ? 'Owner Type'
            : field.attributeDisplayName || field.attributeName,

        existingValue:
          field.attributeName === 'securitysystems.systemname' ? '' : '',
        ...(field.attributeName === 'securitysystems.systemname' && {
          showError: false,
          errorMessage: '',
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        ...(field.attributeName.includes('owner') && { disabled: true }),
      };
    }
  });

  // Build Connection category
  const connectionUncommittedValues: Record<string, any> = {};
  const connectionFields = parsedTemplates?.Connection || [];

  connectionFields.forEach((field: any) => {
    if (
      field.attributeName &&
      connectionData[field.attributeName] !== undefined
    ) {
      const key = String(field.attributeKey);

      connectionUncommittedValues[key] = {
        submitId: field.attributeName,
        value: String(connectionData[field.attributeName] || ''),
        attributeKey: field.attributeKey,
        identifiers: {
          group: 'Basic',
          category: 'Connection',
          subcategory: 'Configuration',
        },
        name: field.attributeDisplayName || field.label || field.attributeName,
        existingValue: null,
      };
    }
  });

  // Add ConnectionJSON
  const connectionJSONTemplateData = {
    template: buildConnectionJSONTemplate(connectionData),
  };

  const processedTemplate = replacePlaceholders(
    connectionJSONTemplateData,
    userPrompt
  );
  const connectionJSONTemplate = processedTemplate.template as string;

  // Find ConnectionJSON field from parsed templates or use default key
  const connectionJSONField = connectionFields.find(
    (f: any) => f.attributeName === 'externalconnattvalue.ConnectionJSON'
  );
  const connectionJSONKey = connectionJSONField?.attributeKey || 2703;

  connectionUncommittedValues[String(connectionJSONKey)] = {
    submitId: 'externalconnattvalue.ConnectionJSON',
    value: connectionJSONTemplate,
    attributeKey: connectionJSONKey,
    identifiers: {
      group: 'Basic',
      category: 'Connection',
      subcategory: 'Configuration',
    },
    name: 'externalconnattvalue.ConnectionJSON',
    existingValue: null,
  };

  // Build deployData
  const deployData: Record<string, any> = {
    multipleconnection: false,
    'externalconnectiontype.connectionType': 'CUA',
    'externalconnattvalue.ConnectionJSON': connectionJSONTemplate,
    // removed as CUA team is not validating.
    // 'externalconnattvalue.AppType': 'CUA',
  };

  // Add ConfigJSON from categoryTemplates
  if (categoryTemplates?.configuration?.groups) {
    const basicGroup = categoryTemplates.configuration.groups.find(
      (group: any) => group.name === 'Basic'
    );
    const configJSONField = basicGroup?.attributes?.find(
      (field: any) => field.attributeName === 'externalconnattvalue.ConfigJSON'
    );

    if (configJSONField && configJSONField.attributeDefaultValue) {
      deployData['externalconnattvalue.ConfigJSON'] =
        configJSONField.attributeDefaultValue;
    }
  }

  // Add general data to deployData
  Object.entries(generalData).forEach(([key, value]) => {
    if (key === 'endpoints.ownerkey') {
      // Handle both string and object formats
      if (typeof value === 'string' && value.startsWith('{')) {
        try {
          const parsed = JSON.parse(value);

          deployData[key] = parsed.id;
        } catch {
          deployData[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        deployData[key] = (value as { id: string }).id;
      } else {
        deployData[key] = value;
      }
    } else if (key === 'endpoints.ownerType') {
      deployData[key] = Array.isArray(value) ? value : [String(value)];
    } else {
      deployData[key] = value;
    }
  });

  // Add connection data to deployData
  Object.entries(connectionData).forEach(([key, value]) => {
    deployData[key] = value;
  });

  const payload = {
    saveCategoryRequest: {
      appTypeKey,
      draftType,
      endpointName,
      categories: [
        {
          categoryName: 'General',
          categoryValue: {
            uncommitedValues: generalUncommittedValues,
          },
        },
        {
          categoryName: 'Connection',
          categoryValue: {
            uncommitedValues: connectionUncommittedValues,
            connectionType: 'CUA',
          },
        },
        {
          categoryName: 'modules',
          categoryValue: 'BASIC_CONFIGURATION,REQUEST_CONFIGURATION',
        },
      ],
    },
    deployData,
    deploy: true,
  };

  return (await services.initiateOnboard(payload)) as OnboardResult;
};

export const testConnection = async (
  services: ApiServices,
  generalData: Record<string, unknown>
): Promise<unknown> => {
  if (!services.testConnection) {
    throw new Error('Test connection API not available');
  }

  const endpointName = String(generalData['securitysystems.systemname'] || '');

  return services.testConnection({ endpointName });
};
